import axios from 'axios'
import { LOGOUT, EXPIRED_SESSION } from '@/vuex/types'
import { app } from '@/main'

let instance = axios.create({
  baseURL: '/api',
  timeout: 30000
})

instance.interceptors.request.use(function (config) {
  let token = window.sessionStorage.getItem('token')
  if (token) {
    config.headers.common['Authorization'] = 'Bearer ' + token
  }
  let loadingDiv = document.createElement('div')
  let spinnerDiv = document.createElement('div')
  loadingDiv.id = 'loadingDiv'
  spinnerDiv.className = 'fa fa-spinner fa-3x fa-pulse'
  loadingDiv.className = 'loading text-dark'
  let exists = document.getElementById('loadingDiv')
  if (!exists) {
    document.body.appendChild(loadingDiv)
    loadingDiv.appendChild(spinnerDiv)
    loadingDiv.setAttribute('data-requests', 1)
    loadingDiv.setAttribute('data-responses', 0)
  } else {
    let count = parseInt(exists.getAttribute('data-requests'))
    exists.setAttribute('data-requests', count + 1)
  }
  return config
}, function (error) {
  return Promise.reject(error.response)
})

instance.interceptors.response.use(function (response) {
  let loadingDivRemove = document.getElementById('loadingDiv')
  if (loadingDivRemove) {
    let count = parseInt(loadingDivRemove.getAttribute('data-responses'))
    loadingDivRemove.setAttribute('data-responses', count + 1)
    let requests = parseInt(loadingDivRemove.getAttribute('data-requests'))
    let responses = parseInt(loadingDivRemove.getAttribute('data-responses'))
    if (requests === responses) {
      loadingDivRemove.parentNode.removeChild(loadingDivRemove)
    }
  }
  return response
}, function (error) {
  let loadingDivRemove = document.getElementById('loadingDiv')
  if (loadingDivRemove) {
    let count = parseInt(loadingDivRemove.getAttribute('data-responses'))
    loadingDivRemove.setAttribute('data-responses', count + 1)
    let requests = parseInt(loadingDivRemove.getAttribute('data-requests'))
    let responses = parseInt(loadingDivRemove.getAttribute('data-responses'))
    if (requests === responses) {
      loadingDivRemove.parentNode.removeChild(loadingDivRemove)
    }
  }
  if (error.response && error.response.status === 401) {
    app.$store.commit(EXPIRED_SESSION, true)
    app.$store.dispatch(LOGOUT)
    let redirect = app.$router.currentRoute.path
    if (!redirect.includes('login')) {
      redirect = '/login'
      app.$router.push(redirect)
    }
    return Promise.resolve(redirect)
  } else {
    return Promise.reject(error.response)
  }
})

class Api {
  async getForm (permalink, params = {}, auth = 'admin') {
    try {
      let { data } = await instance.get(`${auth}/form`, {
        params: {
          permalink,
          ...params
        }
      }, { timeout: permalink === 'addSmartCard' ? 100000 : 15000 })
      return data.form
    } catch (err) {
      throw err
    }
  }

  async submitForm (formData, method = 'post', auth = 'admin') {
    try {
      let { data } = await instance[method](`${auth}/form`, formData, { timeout: 30000 })
      return data.result
    } catch (err) {
      throw err
    }
  }

  async getReservation (reservationUuid) {
    let { data } = await instance.get('admin/object/reservation', {
      params: { reservationUuid }
    })
    return data.object
  }

  async getAdminRequest (problemUuid) {
    let { data } = await instance.get('admin/object/adminRequest', {
      params: { problemUuid }
    })
    return data.object
  }

  async getTicket (ticketId) {
    let { data } = await instance.get('admin/object/ticket', {
      params: { ticketId }
    })
    return data.object
  }

  async closeTicket (ticketId) {
    let { data } = await instance.post('admin/object/ticket/close', { ticketId })
    return data.object
  }

  async reopenTicket (ticketId) {
    let { data } = await instance.post('admin/object/ticket/reopen', { ticketId })
    return data.object
  }

  async deleteTicket (ticketId) {
    let { data } = await instance.post('admin/object/ticket/delete', { ticketId })
    return data.object
  }

  async removeTicketPriority (ticketId) {
    let { data } = await instance.post('admin/object/ticket/removePriority', { ticketId })
    return data.object
  }

  async addTicketPriority (ticketId) {
    let { data } = await instance.post('admin/object/ticket/addPriority', { ticketId })
    return data.object
  }

  async getReservationCosts (reservationUuid) {
    let { data } = await instance.get('admin/object/reservationCosts', {
      params: { reservationUuid }
    })
    return data.object
  }

  async getUpcomingReservations (carUuid) {
    let { data } = await instance.get('admin/object/getUpcomingReservations', {
      params: { carUuid }
    })
    return data.object
  }

  async getVacantLots (params) {
    let { data } = await instance.post('admin/object/getVacantLots', params)
    return data.object
  }

  async getCarReservations (carId) {
    let { data } = await instance.get('admin/object/car/reservations', {
      params: {
        carId,
        limit: 10,
        offset: 0
      }
    })
    return data
  }

  async getUser (accountUuid) {
    let { data } = await instance.get('/admin/object/account', {
      params: { accountUuid }
    })
    return data.object
  }

  async getApplication (applicationUuid) {
    let { data } = await instance.get('/admin/object/application', {
      params: { applicationUuid }
    })
    return data.object
  }

  async getHeldCharge (heldChargeUuid) {
    let { data } = await instance.get('/admin/object/heldCharge', {
      params: { heldChargeUuid }
    })
    return data.object
  }

  async getAccountMessages (heldChargeUuid) {
    let { data } = await instance.get('/admin/object/accountMessages')
    if (!data) throw new Error('Could not find your session')
    return data.object
  }

  async dismissNotifications () {
    await instance.get('/admin/object/dismissNotifications')
  }

  async dismissNotification (accountMessageUuid) {
    let { data } = await instance.post('admin/object/dismissNotification', {
      accountMessageUuid
    })
    return data.object
  }

  async snoozeNotification (accountMessageUuid) {
    let { data } = await instance.post('admin/object/snoozeNotification', {
      accountMessageUuid
    })
    return data.object
  }

  async snoozeNotifications () {
    let { data } = await instance.get('/admin/object/snoozeNotifications')
    return data.object
  }

  async getReservationAvailability (startDate, carUuid) {
    let { data } = await instance.get('admin/object/car/availability', {
      params: {
        startDate,
        carUuid
      }
    })
    return data
  }

  async getReservationHistory (reservationUuid) {
    let { data } = await instance.get('admin/object/reservation/history', {
      params: { reservationUuid }
    })
    return data
  }

  async getReservationBreakdowns (reservationUuid) {
    let { data } = await instance.get('admin/object/reservation/breakdowns', {
      params: { reservationUuid }
    })
    return data
  }

  async getCar (carUuid, startTime, endTime) {
    let { data } = await instance.get('/admin/object/car', {
      params: {
        carUuid,
        startTime,
        endTime
      }
    })
    return data.object
  }

  async getContract (contractId) {
    let { data } = await instance.get('/admin/object/contract', {
      params: {
        contractId
      }
    })
    return data.object
  }

  // To get the details of a Trailing Rebate promo code.
  async getTrailingRebatePromo (trailingRebatePromoId) {
    let { data } = await instance.get('/admin/object/trailingRebatePromo', {
      params: {
        trailingRebatePromoId
      }
    })
    return data.object
  }

  async getCarList (driverUuid) {
    let { data } = await instance.get('/admin/object/cars', {
      params: {
        driverUuid
      }
    })
    return data.object
  }

  async getCarAvailability (carUuid, startDate, proposedStartTime, proposedEndTime) {
    let { data } = await instance.get('/admin/object/car/availability', {
      params: {
        carUuid,
        startDate,
        proposedStartTime,
        proposedEndTime
      }
    })
    return data
  }

  async getCarRates (carUuid) {
    let { data } = await instance.get('/admin/object/car/rates', {
      params: { carUuid }
    })
    return data.object
  }

  async getCarFeatures (accountUuid = false) {
    let { data } = await instance.get('/admin/object/car/features', {
      params: { accountUuid }
    })
    return data
  }

  async getRates () {
    let { data } = await instance.get('/admin/object/rates', {})
    return data.object
  }

  async getExtras (carUuid) {
    let { data } = await instance.get('/admin/object/extras', {
      params: { carUuid }
    })
    return data.object
  }

  async editCreditCardType (CreditCardUuid) {
    let { data } = await instance.post('/admin/object/editCreditCardType', {
      CreditCardUuid
    })
    return data.object
  }

  async instantReservation (vehicleId) {
    let { data } = await instance.post('admin/object/reservation/instant', {
      vehicleId
    })
    return data
  }

  async loadOrganization (domain) {
    let { data } = await instance.post('public/config')
    return data
  }

  async unlockCar (carUuid) {
    let { data } = await instance.post('admin/object/car/unlock', {
      carUuid
    })
    return data
  }

  async lockCar (carUuid) {
    let { data } = await instance.post('admin/object/car/lock', {
      carUuid
    })
    return data
  }

  async ignitionOn (carUuid) {
    let { data } = await instance.post('admin/object/car/ignitionOn', {
      carUuid
    })
    return data
  }

  async ignitionOff (carUuid) {
    let { data } = await instance.post('admin/object/car/ignitionOff', {
      carUuid
    })
    return data
  }

  async captureCard (carUuid) {
    let { data } = await instance.post('admin/object/car/captureCard', {
      carUuid
    })
    return data
  }

  async addCreditCard (token, AccountUuid) {
    let { data } = await instance.post('admin/object/addCreditCard', {
      token, AccountUuid
    })
    return data.object
  }

  async immobilizeVehicle (carUuid) {
    let { data } = await instance.post('admin/object/car/immobilize', {
      carUuid
    })
    return data
  }

  async gpsLocation (carUuid) {
    let { data } = await instance.post('admin/object/car/gps', {
      carUuid
    })
    return data
  }

  async resendReservation (reservationUuid) {
    let { data } = await instance.post('admin/object/reservation/resend', {
      reservationUuid
    })
    return data
  }

  async techPushValet (valetUuid) {
    let { data } = await instance.post('admin/object/valet/resend', {
      valetUuid
    })
    return data
  }

  async deleteValet (valetUuid) {
    let { data } = await instance.post('admin/object/valet/delete', {
      valetUuid
    })
    return data
  }

  async requestTripData (reservationUuid) {
    let { data } = await instance.post('admin/object/reservation/tripData', {
      reservationUuid
    })
    return data
  }

  async refreshLoggedInUser () {
    let { data } = await instance.post('admin/object/refreshLoggedInUser')
    if (!data) throw new Error('Could not restore your session')
    return data
  }

  async resolveNote (note) {
    let { data } = await instance.post('admin/object/note/resolve', { note })
    return data.note
  }

  async toggleAlert (payload) {
    let { data } = await instance.post('admin/object/toggleAlert', payload)
    return data.object
  }

  async resolveDamage (damage) {
    let { data } = await instance.post('admin/object/damage/resolve', { damage })
    return data.damage
  }

  async checkUsername (username) {
    let { data } = await instance.post('admin/object/uniqueUsername', {
      username
    })
    return data.object
  }

  async checkPromoCode (code) {
    let { data } = await instance.post('admin/object/uniquePromoCode', {
      code
    })
    return data.object
  }

  async getRebatePromoIncome (options) {
    let { data } = await instance.post('admin/object/getRebatePromoIncome', {
      ...options
    })
    return data.object
  }

  async searchUser (q) {
    let { data } = await instance.get('admin/object/search/user', {
      params: { q }
    })
    return data
  }

  async searchAdmin (q) {
    let { data } = await instance.get('admin/object/search/admin', {
      params: { q }
    })
    return data
  }

  async searchSmartcard (q) {
    let { data } = await instance.get('admin/object/search/smartcard', {
      params: { q }
    })
    return data
  }

  async compileEmailTemplate (template) {
    let { data } = await instance.post('admin/object/compileEmailTemplate', {
      template
    })
    return data.object
  }

  async validateReservationPromo (options) {
    let { data } = await instance.post('admin/object/reservationPromo/validate', {
      code: options.code,
      car: options.car,
      user: options.user,
      startDate: options.startDate,
      endDate: options.endDate
    })
    return data
  }

  async getSidebar () {
    let { data } = await instance.get('admin/object/sidebar/counts', {})
    return data
  }

  async getDashboard (payload) {
    let { data } = await instance.post('admin/object/dashboard', payload)
    return data.object
  }

  async getDashboardMessages (payload) {
    let { data } = await instance.post('admin/object/dashboardMessages', payload)
    return data.object
  }

  async sendApplicationLink (applicationUuid) {
    let { data } = await instance.post('admin/object/sendApplicationLink', {
      uuid: applicationUuid
    })
    return data.object
  }

  async moveApplication (applicationUuid) {
    let { data } = await instance.post('admin/object/application/complete', {
      uuid: applicationUuid
    })
    return data.object
  }

  async deleteApplication (applicationUuid) {
    let { data } = await instance.post('admin/object/application/deleted', {
      uuid: applicationUuid
    })
    return data.object
  }

  async approveApplication (applicationUuid) {
    let { data } = await instance.post('admin/object/application/approved', {
      uuid: applicationUuid
    })
    return data.object
  }

  async flagApplication (applicationUuid) {
    let { data } = await instance.post('admin/object/application/flagged', {
      uuid: applicationUuid
    })
    return data
  }

  async unflagApplication (applicationUuid) {
    let { data } = await instance.post('admin/object/application/unflagged', {
      uuid: applicationUuid
    })
    return data
  }

  async checkApplication (applicationUuid) {
    let { data } = await instance.post('admin/object/application/checked', {
      uuid: applicationUuid
    })
    return data
  }

  async closeLot (LotUuid) {
    let { data } = await instance.post('admin/object/closeLot', {
      uuid: LotUuid
    })
    return data.object
  }

  async openLot (LotUuid) {
    let { data } = await instance.post('admin/object/openLot', {
      uuid: LotUuid
    })
    return data.object
  }

  async toggleLotOvernight (LotUuid) {
    let { data } = await instance.post('admin/object/toggleLotOvernight', {
      uuid: LotUuid
    })
    return data.object
  }

  async toggleLotWeekend (LotUuid) {
    let { data } = await instance.post('admin/object/toggleLotWeekend', {
      uuid: LotUuid
    })
    return data.object
  }

  async toggleLotOneWayTrips (LotUuid) {
    let { data } = await instance.post('admin/object/toggleLotOneWayTrips', {
      uuid: LotUuid
    })
    return data.object
  }

  async chargeMembershipFee (applicationUuid) {
    try {
      let { data } = await instance.post('admin/object/application/charge', {
        uuid: applicationUuid
      })
      return data.success
    } catch (e) {
      throw e
    }
  }

  async loginAsUser (account) {
    let { data } = await instance.post('admin/object/loginAsUser', {
      uuid: account.uuid
    })
    return data.object
  }

  async logout () {
    return await instance.post('public/logout')
  }

  async sendGuarantorForm (applicationUuid) {
    let { data } = await instance.post('admin/object/application/guarantorForm', {
      uuid: applicationUuid
    })
    return data.object
  }

  async getCards (options) {
    let { data } = await instance.post(`admin/cards/${options.cards}`, {
      searchForm: options.searchForm,
      sortDirection: options.sortDirection,
      sortColumn: options.sortColumn,
      offset: options.offset,
      limit: options.limit,
      newSearch: options.newSearch,
      selectedOrganization: options.selectedOrganization
    })
    return { ...data, offset: options.offset, limit: options.limit, permalink: options.cards }
  }

  async generateWashList (options) {
    let { data } = await instance.post('admin/object/washList', { ...options })
    return data.object
  }

  async updateWashList (cars) {
    let { data } = await instance.post('admin/object/washList/update', { cars })
    return data.object
  }

  async generateBatteryList (options) {
    let { data } = await instance.post('admin/object/batteryList', { ...options }, { timeout: 30000 })
    return data.object
  }

  async getApplicationAnalytics (options) {
    let { data } = await instance.post('admin/object/refreshApplicationChart', { ...options }, { timeout: 30000 })
    return data.object
  }

  async getAccountReporting (options) {
    let { data } = await instance.post('admin/object/accountReport', { ...options }, { timeout: 30000 })
    return data.object
  }

  async getCarData (options) {
    let { data } = await instance.post('admin/object/refreshCarChart', { ...options }, { timeout: 30000 })
    return data.object
  }

  async searchCars (options) {
    let { data } = await instance.post('admin/object/searchCars', { ...options })
    return data.object
  }

  async getUserCars (options) {
    let { data } = await instance.post('admin/object/userCars', { ...options })
    return data.object
  }

  async getOrgPromos (options) {
    let { data } = await instance.post('admin/object/orgPromos', { ...options })
    return data.object
  }

  async chargeAndExport (options) {
    let { data } = await instance.post('admin/object/tolls/chargeExport', { ...options })
    return data.object
  }

  async getAccountType (options) {
    let { data } = await instance.post('admin/object/accountType', { ...options })
    return data.object
  }
}
export default Api



// WEBPACK FOOTER //
// ./src/vuex/api/index.js